import { useEffect, useState } from "react";
import { Row, Col } from "react-bootstrap";
import { useForm, FormProvider, useWatch } from "react-hook-form";
import { useIntl, FormattedMessage } from "react-intl";
import { useStateMachine } from "little-state-machine";

import {
  createPrefill,
  getPaymentLocationFormDetails,
  getFreightModes,
  getCountries,
  getPrefillDetails,
  updatePrefill,
  deletePrefill,
  getCompanyAddressBook,
} from "api";
import { getLabelWithTranslationFallback } from "helpers/getLabel";
import LoadingSkeleton from "components/atoms/LoadingSkeleton/LoadingSkeleton";
import Button from "components/atoms/Button/Button";
import Hr from "components/atoms/Hr/Hr";
import Dialog from "components/organisms/Dialog/Dialog";
import errorToast from "helpers/errorToast";
import Dropdown from "components/atoms/Dropdown/Dropdown";
import Input from "components/atoms/Input/Input";
import Checkbox from "components/atoms/Checkbox/Checkbox";
import {
  EXPORT_CLEARANCE_OPTIONS,
  INCOTERM_OPTIONS,
} from "pages/shipper/RequestForm/components/Service/ServiceFormDefaultOptions";
import { inputValues, outputValues, insuranceOptions } from "./prefillsConstants";
import SearchSelect from "../../../RequestForm/components/Common/SearchSelect";

interface IPrefillsDialogProps {
  show: boolean;
  close: () => void;
  data: any;
  fetchData: () => void;
  selectedPrefill?: { id: number } | null;
}

function PrefillsDialog({ show, close, selectedPrefill, fetchData }: IPrefillsDialogProps) {
  const { state } = useStateMachine({}) as any;

  const intl = useIntl();
  const methods = useForm();
  const { handleSubmit, getValues, setValue, control, reset } = methods;
  const { locations } = JSON.parse(localStorage.getItem("cargobase_userpermissions") || "").payment_location;
  const paymentLocation = useWatch({ name: "location", control });
  const inputField = useWatch({ name: "input_field", control });
  const outputField = useWatch({ name: "output_field", control });

  const [loadingInputs, setLoadingInputs] = useState<boolean>(false);
  const [availableInputs, setAvailableInputs] = useState<any[]>([]);
  const [freightModes, setFreightModes] = useState<any[]>([]);
  const [countries, setCountries] = useState<any[]>([]);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [addressBookOptions, setAddressBookOptions] = useState(
    state.addressBook.map((address: { name: string }) => ({
      value: address.name,
      label: address.name,
    })),
  );

  // Update the values of the form when a prefill is selected
  useEffect(() => {
    if (!selectedPrefill) return;
    setLoadingInputs(true);
    getPrefillDetails(selectedPrefill.id)
      .then((res) => {
        if (res.data.input_field.includes("invited_modes")) res.data.input_field = "invited_modes";
        setValue("location", res.data.location.key);
        setValue("input_field", res.data.input_field);
        setValue("input_field_label", res.data.input_field_label);
        setValue("output_field", res.data.output_field);
        setValue("output_field_label", res.data.output_field_label);
        setValue("input_value", res.data.input_value);
        setValue("output_value", res.data.output_value);
        setValue("disabled", res.data.disabled);
      })
      .catch((err) => errorToast(err))
      .finally(() => setLoadingInputs(false));
  }, [selectedPrefill]);

  // Load all input values after a location is selected
  useEffect(() => {
    if (!paymentLocation) return;
    setLoadingInputs(true);
    getFreightModeOptions();
    getPaymentLocationFormDetails(getValues("location"))
      .then((res) => {
        const inputs = res.data.organizational_options.map((input: any) => ({
          key: `organizational.${input.field_id}`,
          ...(input?.dropdown_values?.length > 0 && { dropdown_options: input.dropdown_values }),
        }));
        setAvailableInputs(inputs);
      })
      .catch((err) => errorToast(err))
      .finally(() => setLoadingInputs(false));
    if (state.addressBook.length === 0) {
      getCompanyAddressBook({
        params: {
          payment_location: paymentLocation,
          page_size: 20,
        },
      }).then((res) => {
        const addressOptions = res.data.results.map((address) => ({
          value: address.name,
          label: address.name,
        }));
        setAddressBookOptions(addressOptions);
      });
    }
  }, [paymentLocation]);

  useEffect(() => {
    getCountries()
      .then((res) => setCountries(res.data))
      .catch((err) => errorToast(err));
  }, []);

  const getFreightModeOptions = () => {
    getFreightModes({
      params: {
        locations: paymentLocation,
      },
    })
      .then((res) => {
        setFreightModes(res.data.map((mode: any) => ({ value: mode.key, label: mode.value })));
      })
      .catch((err) =>
        errorToast(
          err,
          intl.formatMessage({
            id: "general.couldNotRetrieveFreightModes",
            defaultMessage: "Could not retrieve freight modes. Please try again later.",
          }),
        ),
      );
  };

  // Populates the dropdown with correct values based on the selected input field
  const populateDropdown = (input: string) => {
    switch (input) {
      case "invited_modes":
        return freightModes;
      case "schedule.pickup_country":
      case "schedule.stop1_country":
        return countries;
      case "service.export_clearance":
      case "service.import_clearance":
      case "service.transit_clearance":
        return EXPORT_CLEARANCE_OPTIONS(intl);
      case "service.insurance":
        return insuranceOptions(intl);
      case "service.incoterms":
        return INCOTERM_OPTIONS(intl);
      default:
        return [];
    }
  };

  const resetValues = (resetLocation: boolean = false) => {
    reset({
      location: resetLocation ? "" : getValues("location"),
      input_field: "",
      output_field: "",
      input_value: "",
      output_value: "",
      input_field_label: "",
      output_field_label: "",
      input_value_label: "",
      output_value_label: "",
      disabled: false,
    });
  };

  // Renders an input value based on the selected input field.
  const renderValue = (type: string) => {
    let selectedValue: any;
    let name: string;

    if (type === "input") {
      selectedValue = inputValues(paymentLocation, intl).find((input: any) => input.value === inputField);
      name = "input_value";
    } else {
      selectedValue = outputValues(paymentLocation, intl).find((output: any) => output.value === outputField);
      name = "output_value";
    }

    // If a company name is selected as input, render a searchSelect component with addressbook options.
    if (selectedValue?.value === "schedule.pickup_address_name" || selectedValue?.value === "schedule.stop1_address_name") {
      return (
        <div className="w-192 mr-8 payment-location-input">
          <SearchSelect
            options={addressBookOptions}
            name={name}
            required
            placeholder={intl.formatMessage({
              id: "new-request.schedule.searchCompany",
              defaultMessage: "Search company name",
            })}
          />
        </div>
      );
    }

    const inputDropdownOptions = availableInputs.find((input) => input.key === selectedValue?.value)?.dropdown_options;

    const { fixed_value } = selectedValue || {};

    if (inputDropdownOptions && inputDropdownOptions.length > 0) {
      return (
        <div className="w-192-px">
          <Dropdown
            required
            useHookForm
            onChange={(value: { label: string }) => {
              setValue(`${name}_label`, value?.label || ``);
            }}
            name={name}
            disabled={!selectedValue}
            options={inputDropdownOptions.map((option: any) => ({ value: option.value, label: option.key }))}
            placeholder={intl.formatMessage({
              id: "general.pleaseSelect",
              defaultMessage: "Please select",
            })}
          />
        </div>
      );
    }

    if (!fixed_value)
      return (
        <Input
          name={name}
          placeholder={intl.formatMessage({
            id: "general.typeHere",
            defaultMessage: "Type here...",
          })}
          className="rounded-input input-width-md"
          disabled={!selectedValue}
          required
        />
      );

    if (fixed_value)
      return (
        <div className="w-192-px">
          <Dropdown
            required
            useHookForm
            onChange={(value: { label: string }) => {
              setValue(`${name}_label`, value?.label || ``);
            }}
            name={name}
            disabled={!selectedValue}
            options={populateDropdown(selectedValue.value)}
            placeholder={intl.formatMessage({
              id: "general.pleaseSelect",
              defaultMessage: "Please select",
            })}
          />
        </div>
      );
  };

  const closeDialog = () => {
    setValue("payment_location", "");
    methods.reset();
    close();
  };

  const onSubmit = (data: any) => {
    if (data.input_field === "invited_modes") {
      data.input_field = `invited_modes.${data.input_value}`;
    }

    setIsSubmitting(true);
    const payload = {
      ...data,
    };
    if (selectedPrefill) {
      updatePrefill(selectedPrefill.id, payload)
        .then(() => {
          fetchData();
          closeDialog();
        })
        .catch((err) => errorToast(err))
        .finally(() => setIsSubmitting(false));
    } else {
      createPrefill(payload)
        .then(() => {
          fetchData();
          closeDialog();
        })
        .catch((err) => errorToast(err))
        .finally(() => setIsSubmitting(false));
    }
  };

  const handleDeletePrefill = () => {
    if (!selectedPrefill) return;
    setIsDeleting(true);
    deletePrefill(selectedPrefill.id)
      .then(() => {
        fetchData();
        closeDialog();
      })
      .catch((err) => errorToast(err))
      .finally(() => setIsDeleting(false));
  };

  return (
    <Dialog
      show={show}
      onHide={closeDialog}
      title={
        !selectedPrefill
          ? intl.formatMessage({
              id: "settings.prefills.addPrefill",
              defaultMessage: "Add prefill",
            })
          : intl.formatMessage({
              id: "settings.prefills.editPrefill",
              defaultMessage: "Edit prefill",
            })
      }
    >
      <FormProvider {...methods}>
        <form className="w-100" onSubmit={handleSubmit(onSubmit)}>
          <Row className="mb-3">
            <Col>
              {getLabelWithTranslationFallback("Payment location", {
                style: "capitalize",
                translation: <FormattedMessage id="report.paymentLocation" defaultMessage="Payment location" />,
              })}
            </Col>
            <Col>
              <div className="w-192 mr-8 payment-location-input">
                <SearchSelect
                  onSelect={() => resetValues(false)}
                  options={locations}
                  name="location"
                  required
                  placeholder={intl.formatMessage({
                    id: "new-request.organisational.chooseCompany",
                    defaultMessage: "Choose payment location...",
                  })}
                />
              </div>
            </Col>
          </Row>
          <Hr />
          {loadingInputs && <LoadingSkeleton rows={2} />}
          {!loadingInputs && paymentLocation && (
            <div>
              <Row>
                <Col>
                  <FormattedMessage id="settings.prefills.inputField" defaultMessage="Input Field" />
                </Col>
                <Col>
                  <div className="w-192-px mb-3">
                    <Dropdown
                      useHookForm
                      onChange={(value: { label: string }) => {
                        setValue("input_field_label", value.label);
                        setValue("input_value", "");
                        setValue("input_value_label", "");
                      }}
                      required
                      name="input_field"
                      options={inputValues(paymentLocation, intl).filter(
                        (inputValue) =>
                          availableInputs.some((availableInput) => availableInput.key === inputValue.value) ||
                          inputValue.available,
                      )}
                      placeholder={intl.formatMessage({
                        id: "general.pleaseSelect",
                        defaultMessage: "Please select",
                      })}
                    />
                  </div>
                </Col>
              </Row>
              <Row>
                <Col>
                  <FormattedMessage id="settings.prefills.inputValue" defaultMessage="Input value" />
                </Col>
                <Col>{renderValue("input")}</Col>
              </Row>
              <Hr />
              <Row>
                <Col>
                  <FormattedMessage id="settings.prefills.outputField" defaultMessage="Output field" />
                </Col>
                <Col>
                  <div className="w-192-px mb-3">
                    <Dropdown
                      required
                      useHookForm
                      onChange={(value: { label: string }) => {
                        setValue("output_field_label", value.label);
                        setValue("output_value", "");
                        setValue("output_value_label", "");
                      }}
                      name="output_field"
                      options={outputValues(paymentLocation, intl).filter(
                        (outputValue) =>
                          availableInputs.some((availableInput) => availableInput.key === outputValue.value) ||
                          outputValue.available,
                      )}
                      placeholder={intl.formatMessage({
                        id: "general.pleaseSelect",
                        defaultMessage: "Please select",
                      })}
                    />
                  </div>
                </Col>
              </Row>
              <Row>
                <Col>
                  <FormattedMessage id="settings.prefills.outputValue" defaultMessage="Output value" />
                </Col>
                <Col>{renderValue("output")}</Col>
              </Row>
              <Hr />
              <Row>
                <Col>
                  <FormattedMessage id="general.disabled" defaultMessage="Disabled" />
                </Col>
                <Col>
                  <Checkbox name="disabled" providerSections />
                </Col>
              </Row>
            </div>
          )}
          <div className="d-flex mt-3 justify-content-between w-100">
            {selectedPrefill && (
              <Button className="mr-2" variant="danger" isLoading={isDeleting} onClick={handleDeletePrefill}>
                <FormattedMessage id="general.delete" defaultMessage="Delete" />
              </Button>
            )}
            <div className="d-flex ml-auto">
              <Button className="mr-2" variant="outline-primary" onClick={closeDialog}>
                <FormattedMessage id="general.cancel" defaultMessage="Cancel" />
              </Button>
              <Button isLoading={isSubmitting} className="mr-2" type="submit">
                {selectedPrefill ? (
                  <FormattedMessage id="general.save" defaultMessage="Save" />
                ) : (
                  <FormattedMessage id="general.add" defaultMessage="Add" />
                )}
              </Button>
            </div>
          </div>
        </form>
      </FormProvider>
    </Dialog>
  );
}

PrefillsDialog.defaultProps = {
  selectedPrefill: null,
};

export default PrefillsDialog;
